import React, { type ReactNode, useEffect } from 'react';
import { AppRoot } from '@telegram-apps/telegram-ui';
import {
  useLaunchParams,
  useSignal,
  isMiniAppDark,
  mountMiniApp,
  unmountMiniApp,
  retrieveLaunchParams,
} from '@telegram-apps/sdk-react';
import { setInitDataRaw } from '@/shared/api/client';
import { ErrorBoundary } from '@/shared/ui/ErrorBoundary';
import { TelegramRequiredScreen } from '@/shared/ui/TelegramRequiredScreen';

interface Props {
  children: ReactNode;
}

const IS_DEV_BYPASS =
  import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK_TELEGRAM === 'true';

function Inner({ children }: Props) {
  // Throws ERR_RETRIEVE_LP_FAILED if not in Telegram — ErrorBoundary catches it
  const lp = useLaunchParams();
  const isDark = useSignal(isMiniAppDark);

  useEffect(() => {
    try {
      mountMiniApp();
    } catch {
      // Not critical — some Telegram clients don't support this call
    }
    try {
      const { initDataRaw } = retrieveLaunchParams();
      setInitDataRaw(initDataRaw ?? null);
    } catch {
      // Not critical — API calls will return 401 without auth header
    }
    return () => {
      try {
        unmountMiniApp();
      } catch {
        // ignore
      }
    };
  }, []);

  return (
    <AppRoot
      appearance={isDark ? 'dark' : 'light'}
      platform={['macos', 'ios'].includes(lp.platform) ? 'ios' : 'base'}
      style={FLEX_FILL}
    >
      {children}
    </AppRoot>
  );
}

// In dev bypass mode render children directly inside AppRoot without Telegram SDK requirements
const FLEX_FILL: React.CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 };

function DevWrapper({ children }: Props) {
  return (
    <AppRoot appearance="light" platform="base" style={FLEX_FILL}>
      {children}
    </AppRoot>
  );
}

export function TelegramProvider({ children }: Props) {
  if (IS_DEV_BYPASS) {
    return <DevWrapper>{children}</DevWrapper>;
  }
  return (
    <ErrorBoundary fallback={<TelegramRequiredScreen />}>
      <Inner>{children}</Inner>
    </ErrorBoundary>
  );
}
